import React, { useEffect, useRef, useState } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";

const Hero = () => {
  const videoRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const [showSlider, setShowSlider] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    { img: "/images/property1.jpg", title: "The Graham", location: "Tse Addo, Accra" },
    { img: "/images/property2.jpg", title: "Modern Smart Apartment", location: "Airport Residential Area" },
    { img: "/images/property3.jpg", title: "Beachfront Family House", location: "Labadi Beach, Accra" },
    { img: "/images/property4.jpg", title: "Ultra-Premium Penthouse", location: "Cantonments, Accra" },
  ];

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnded = () => {
      clearTimeout(fallbackTimerRef.current);
      setTimeout(() => setShowSlider(true), 300);
    };

    const handleLoadedMetadata = () => {
      const duration = video.duration;
      if (duration && isFinite(duration) && duration > 0) {
        fallbackTimerRef.current = setTimeout(() => setShowSlider(true), (duration + 0.5) * 1000);
      }
    };

    const attemptPlay = () => {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          fallbackTimerRef.current = setTimeout(() => setShowSlider(true), 800);
        });
      }
    };

    video.addEventListener("ended", handleEnded);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    attemptPlay();

    return () => {
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      clearTimeout(fallbackTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let interval;
    if (showSlider) {
      interval = setInterval(() => setCurrentSlide((prev) => (prev + 1) % slides.length), 6000);
    }
    return () => clearInterval(interval);
  }, [showSlider, slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <section className="relative w-full h-screen overflow-hidden">
      {/* SEO Meta + Structured Data */}
      <Helmet>
        <title>Solonet Properties | Luxury Homes, Apartments & Real Estate in Ghana</title>
        <meta name="description" content="Discover luxury homes, premium apartments, and real estate investment opportunities in Ghana with Solonet Properties." />
        <meta name="keywords" content="Solonet Properties, Kwame Sarfo, real estate Ghana, luxury homes Ghana, apartments Accra, property sales, property rentals" />
        <meta property="og:title" content="Solonet Properties | Luxury Homes & Apartments in Ghana" />
        <meta property="og:description" content="Explore luxury apartments, modern houses, and beachfront properties across Accra, Ghana with Solonet Properties." />
        <meta property="og:image" content="/images/hero-og.jpg" />
        <meta property="og:url" content="https://solonetproperties.com" />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Solonet Properties",
            "url": "https://solonetproperties.com",
            "logo": "https://solonetproperties.com/images/logo.png",
            "founder": {
              "@type": "Person",
              "name": "Kwame Sarfo"
            },
            "sameAs": [
              "https://www.facebook.com/share/1BHbmAYQDM/?mibextid=wwXIfr",
              "https://www.instagram.com/solo.netgh?igsh=MTR0a2d4NHdyY3BpOA%3D%3D&utm_source=qr"
            ],
            "description": "Premium real estate solutions in Ghana: property sales, rentals, management, and investment consulting."
          }
          `}
        </script>
      </Helmet>

      {/* Background Video */}
      <motion.video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        initial={{ opacity: 1 }}
        animate={{ opacity: showSlider ? 0 : 1 }}
        transition={{ duration: 1 }}
      >
        <source src="/videos/solonet-hero.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </motion.video>

      {/* Image Slider */}
      <AnimatePresence>
        {showSlider && (
          <motion.div
            key="slider"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0"
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={slides[currentSlide].img}
                src={slides[currentSlide].img}
                alt={`${slides[currentSlide].title} - ${slides[currentSlide].location}`}
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                transition={{ duration: 1.2 }}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </AnimatePresence>

            {/* Slide Captions */}
            <motion.div
              key={`caption-${currentSlide}`}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -40 }}
              transition={{ duration: 1 }}
              className="absolute bottom-28 left-1/2 transform -translate-x-1/2 text-center text-white px-4 z-20"
            >
              <h2 className="text-3xl md:text-5xl font-bold drop-shadow-lg">{slides[currentSlide].title}</h2>
              <p className="text-lg md:text-xl mt-2 drop-shadow-md">{slides[currentSlide].location}</p>
            </motion.div>

            {/* Navigation */}
            <button
              onClick={prevSlide}
              className="absolute top-1/2 left-6 -translate-y-1/2 bg-white/20 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg transition"
            >
              <FaArrowLeft size={20} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute top-1/2 right-6 -translate-y-1/2 bg-white/20 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg transition"
            >
              <FaArrowRight size={20} />
            </button>

            {/* Dots */}
            <div className="absolute bottom-8 w-full flex justify-center gap-3">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`h-3 w-3 rounded-full transition-all duration-300 ${
                    currentSlide === idx ? "bg-blue-600 scale-125" : "bg-gray-300 hover:bg-blue-400"
                  }`}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay CTA */}
      <div className="absolute inset-0 bg-black/40 flex flex-col justify-center items-center text-center px-5 z-30">
        <motion.h1
          initial={{ opacity: 0, y: -24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-white text-4xl sm:text-5xl md:text-7xl font-bold mb-4 drop-shadow-xl"
        >
          Find Your Dream Home in Ghana
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 1.2 }}
          className="text-white text-lg md:text-xl max-w-2xl mb-6 drop-shadow-md"
        >
          Discover luxury apartments, modern houses, and premium properties across Accra with Solonet Properties, led by Kwame Sarfo.
        </motion.p>
        <motion.a
          href="/projects"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1.3 }}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg text-lg font-medium shadow-lg transition duration-300"
        >
          View Properties
        </motion.a>
      </div>
    </section>
  );
};

export default Hero;
